import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { GlobalContext } from '@contexts';
import {
  businessAccessQuery,
  businessAccessMutation,
  locationAccessQuery,
  locationAccessMutation,
  siteAccessQuery,
  siteAccessMutation,
  siteQuery,
  templateSiteAccessQuery,
  templateSiteAccessMutation,
  themeQuery,
  usersQuery,
  TEMPLATE_SITE_ACCESS_GROUP,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

import AccessForm from './AccessForm/AccessForm.jsx';
import User from './User/User.jsx';

const locationSiteAccessGroups = (accessGroups) => {
  // is the whitelabel configured for location site access
  //
  // check to make sure the required object/group IDs are set
  if (
    accessGroups &&
    accessGroups.Business &&
    accessGroups.BusinessLocation &&
    accessGroups.Site
  ) {
    return accessGroups;
  }
  return false;
};

export const SiteAccess = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const queryClient = useQueryClient();
  const globals = useContext(GlobalContext);

  // shared state if any access is currently being removed/mutated
  // to avoid issues with multiple attempts to remove access
  // causing problems with access lists in state
  const [disablingInProgress, setDisablingInProgress] = useState(false);
  const updateDisablingInProgress = (v) => {
    // func exposed to child User components to update state
    setDisablingInProgress(v);
  };

  // access supported for this whitelabel/project
  const accessSupported =
    globals.isLoaded &&
    locationSiteAccessGroups(globals.whitelabel.oneoff_access_group_ids);

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // is the whitelabel configured for the optional template site access group
  //
  // `TemplateSite` is opt-in, the Business/BusinessLocation/Site groups are
  // always required and always granted
  const templateSiteAccessEnabled = !!(
    accessSupported && accessSupported.TemplateSite
  );

  // fetch theme
  //
  // needed for template site access (`TemplateSite`), the theme holds the ID
  // of the template site in `Theme.site_id`
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(siteData?.theme_id, {
      enabled: templateSiteAccessEnabled ? !!siteData?.theme_id : false,
    }),
  );

  // ID of the template site to also grant access to
  //
  // `Theme.site_id` is nullable, without it there is no template site to
  // grant access to
  const templateSiteID = templateSiteAccessEnabled
    ? themeData?.site_id || null
    : null;

  // get list of business access users
  const {
    data: businessAccessData,
    isPending: businessAccessIsPending,
    isError: businessAccessIsError,
  } = useQuery(
    businessAccessQuery(siteData?.business_id, {
      enabled: accessSupported ? !!siteData?.business_id : false,
    }),
  );

  // get list of site access users
  const {
    data: siteAccessData,
    isPending: siteAccessIsPending,
    isError: siteAccessIsError,
  } = useQuery(
    siteAccessQuery(siteID, {
      enabled: accessSupported ? !!siteID : false,
    }),
  );

  // get list of template site access users
  const {
    data: templateSiteAccessData,
    isPending: templateSiteAccessIsPending,
  } = useQuery(
    templateSiteAccessQuery(templateSiteID, {
      enabled: !!templateSiteID,
    }),
  );

  // still resolving the optional template site access
  //
  // access is granted/removed by replacing the full list of users, so we
  // can't render the form until we know the current template site list
  const templateSiteAccessIsLoading =
    templateSiteAccessEnabled &&
    ((!!siteData?.theme_id && themeIsPending) ||
      (!!templateSiteID && templateSiteAccessIsPending));

  // get list of location access users
  const {
    data: locationAccessData,
    isPending: locationAccessIsPending,
    isError: locationAccessIsError,
  } = useQuery(
    locationAccessQuery(siteData?.locations[0].id, {
      enabled: accessSupported ? !!siteData?.locations[0].id : false,
    }),
  );

  // get users list
  const {
    data: usersData,
    isPending: usersIsPending,
    isError: usersIsError,
  } = useQuery(
    usersQuery({
      is_active: 1,
      limit: 500,
    }),
  );

  // save business, location, site access
  const mutations = {
    business: useMutation(
      businessAccessMutation(siteData?.business_id, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: ['businesses_access', 'list', siteData?.business_id],
          });
        },
      }),
    ),
    location: useMutation(
      locationAccessMutation(siteData?.locations[0].id, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: ['locations_access', 'list', siteData?.locations[0].id],
          });
        },
      }),
    ),
    site: useMutation(
      siteAccessMutation(siteID, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: ['sites_access', 'list', siteID],
          });
        },
      }),
    ),
    // optional, only mutated when the whitelabel has a `TemplateSite` group
    // and the theme has a template site
    templateSite: useMutation(
      templateSiteAccessMutation(templateSiteID, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: [
              'sites_access',
              'list',
              templateSiteID,
              TEMPLATE_SITE_ACCESS_GROUP,
            ],
          });
        },
      }),
    ),
  };

  // sorted access users by first name
  const sortedAccessData = siteAccessData
    ? siteAccessData.sort((a, b) => a.first_name.localeCompare(b.first_name))
    : [];

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Manage access')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <h1>{t('Manage access')}</h1>
            {!globals.isLoaded && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {globals.isLoaded && !accessSupported && (
              <>
                <Message
                  messageStyle="warning"
                  title="User access not enabled"
                  message="User access is currently not supported on this project. Reach out to your DevHub customer success contact to learn more."
                  fadeoutMessage={false}
                  isFormMessage={false}
                />
                <WelcomeElement>
                  <h1>Introducing User access</h1>
                  <p>
                    User access provides restricted login permissions to
                    individual users on this website. Permission controls enable
                    you to configure which content and fields users can view,
                    edit, and publish.
                  </p>
                </WelcomeElement>
              </>
            )}
            {globals.isLoaded && accessSupported && (
              <>
                {(siteAccessIsPending ||
                  siteIsPending ||
                  businessAccessIsPending ||
                  locationAccessIsPending ||
                  templateSiteAccessIsLoading) && (
                  <Loader className="sb-loader sb-loader-large" />
                )}
                {!siteAccessIsPending &&
                  !businessAccessIsPending &&
                  !locationAccessIsPending &&
                  !templateSiteAccessIsLoading &&
                  (siteAccessIsError || siteAccessData?.length == 0) && (
                    <p>No access granted</p>
                  )}
                {businessAccessData &&
                  locationAccessData &&
                  siteAccessData &&
                  siteData &&
                  !templateSiteAccessIsLoading && (
                    <>
                      <div className="sb-content-view-row">
                        {sortedAccessData.map((user) => {
                          return (
                            <User
                              key={user.id}
                              user={user}
                              disablingInProgress={disablingInProgress}
                              updateDisablingInProgress={
                                updateDisablingInProgress
                              }
                              businessAccessData={businessAccessData}
                              locationAccessData={locationAccessData}
                              siteAccessData={siteAccessData}
                              siteData={siteData}
                              siteID={siteID}
                            />
                          );
                        })}
                      </div>
                      {usersData && (
                        <AccessForm
                          mutations={mutations}
                          disablingInProgress={disablingInProgress}
                          businessAccessData={businessAccessData}
                          locationAccessData={locationAccessData}
                          siteAccessData={siteAccessData}
                          templateSiteAccessData={templateSiteAccessData}
                          templateSiteID={templateSiteID}
                          usersData={usersData}
                        />
                      )}
                    </>
                  )}
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
